import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Briefcase, Laptop, Landmark, Coins, MessageSquare, Instagram, Globe, ExternalLink, Gift } from 'lucide-react';

interface CarouselPartner {
  id: string;
  name: string;
  title: string;
  subtitle: string;
  description: string;
  badge: string;
  url: string;
  ctaText: string;
  gradient: string;
  icon: React.ComponentType<any>;
  iconColor: string;
}

interface PartnerCarouselProps {
  onAnunciarClick?: () => void;
}

export const PartnerCarousel: React.FC<PartnerCarouselProps> = ({ onAnunciarClick }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const carouselPartners: CarouselPartner[] = [
    {
      id: 'consorcio-ademicom',
      name: 'Thayná Hokpat',
      title: 'Thayná Hokpat • Ademicon',
      subtitle: 'Consórcios & Investimentos',
      description: 'Planeje a compra ou renovação do seu carro, moto ou expansão de frota com taxas altamente exclusivas e consultoria sob medida.',
      badge: 'Consórcio Oficial',
      url: 'https://wa.me/554191125987',
      ctaText: 'Falar no WhatsApp 💬',
      gradient: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #1d4ed8 100%)',
      icon: Coins,
      iconColor: 'text-blue-200',
    },
    {
      id: 'marketplace-liberado',
      name: 'Marketplace Grátis',
      title: 'Marketplace 100% Liberado 🚀',
      subtitle: 'Anuncie Grátis e Ganhe Pontos',
      description: 'O acesso à postagem de Stories agora é gratuito e ilimitado para todos os motoristas! Anuncie seus produtos e serviços sem taxas.',
      badge: 'Promoção Ativa',
      url: '',
      ctaText: 'Anunciar Agora 🛒',
      gradient: 'linear-gradient(135deg, #065f46 0%, #10b981 50%, #047857 100%)',
      icon: Gift,
      iconColor: 'text-emerald-200',
    },
    {
      id: 'grupo-whatsapp',
      name: 'Grupo Oficial',
      title: 'Grupo Oficial dos Motoristas 👥',
      subtitle: 'Comunidade Driver Score',
      description: 'Conecte-se com a comunidade de motoristas no WhatsApp, compartilhe dicas sobre a Rhino e fique por dentro das novidades em tempo real.',
      badge: 'Comunidade',
      url: 'https://chat.whatsapp.com/E6IDMCoW4WJJFwNbX3UqpS',
      ctaText: 'Entrar no Grupo 💬',
      gradient: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 50%, #0d9488 100%)',
      icon: MessageSquare,
      iconColor: 'text-teal-200',
    },
    {
      id: 'contabilidade-mei',
      name: 'Marcello M. Bernardo',
      title: 'Marcello M. Bernardo • Contabilidade',
      subtitle: 'Assessoria Especializada em MEI',
      description: 'Regularização de MEI, declaração anual e emissão de Notas Fiscais sem burocracia para motoristas de aplicativo.',
      badge: 'Contabilidade MEI',
      url: 'https://wa.me/5511949862676',
      ctaText: 'Solicitar Suporte 📊',
      gradient: 'linear-gradient(135deg, #1e293b 0%, #475569 50%, #334155 100%)',
      icon: Landmark,
      iconColor: 'text-slate-200',
    },
    {
      id: 'desenvolvimento-tiago',
      name: 'Romanosv3 studio',
      title: 'Romanosv3 studio • Tiago Romão',
      subtitle: 'Sistemas & Automações Inteligentes',
      description: 'Desenvolvimento de robôs para WhatsApp/Telegram, sistemas web/mobile e automação de processos para alavancar seus negócios.',
      badge: 'Soluções Tech',
      url: 'https://wa.me/5511974036983',
      ctaText: 'Criar Projeto 🚀',
      gradient: 'linear-gradient(135deg, #7c2d12 0%, #f97316 50%, #b45309 100%)',
      icon: Laptop,
      iconColor: 'text-orange-200',
    }
  ];

  const nextSlide = () => {
    setActiveIndex((prevIndex) => (prevIndex + 1) % carouselPartners.length);
  };

  const prevSlide = () => {
    setActiveIndex((prevIndex) => (prevIndex - 1 + carouselPartners.length) % carouselPartners.length);
  };

  useEffect(() => {
    if (isHovered) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      return;
    }

    timeoutRef.current = setInterval(nextSlide, 6000);

    return () => {
      if (timeoutRef.current) clearInterval(timeoutRef.current);
    };
  }, [isHovered, activeIndex]);

  const handleBannerClick = (partner: CarouselPartner, e: React.MouseEvent) => {
    // Avoid triggering navigation if user clicks control buttons/dots
    const target = e.target as HTMLElement;
    if (target.closest('.carousel-control')) return;
    
    if (partner.id === 'marketplace-liberado') {
      if (onAnunciarClick) onAnunciarClick();
    } else if (partner.url) {
      window.open(partner.url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div 
      className="relative w-full overflow-hidden rounded-2xl shadow-xl border border-slate-700/50 cursor-pointer select-none group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ height: '150px' }}
    >
      {/* Slides Container */}
      <div className="relative w-full h-full">
        {carouselPartners.map((partner, index) => {
          const isActive = index === activeIndex;
          const Icon = partner.icon;
          
          return (
            <div
              key={partner.id}
              onClick={(e) => handleBannerClick(partner, e)}
              className={`absolute inset-0 w-full h-full flex items-center justify-between p-4 md:p-6 transition-all duration-700 ease-in-out transform ${
                isActive 
                  ? 'opacity-100 translate-x-0 scale-100 pointer-events-auto z-10' 
                  : 'opacity-0 translate-x-8 scale-95 pointer-events-none z-0'
              }`}
              style={{ background: partner.gradient }}
            >
              {/* Glowing Shimmer Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 animate-[shimmer_3s_infinite]" style={{ backgroundSize: '200% 100%' }} />
              
              {/* Background Glassmorphic Accent */}
              <div className="absolute -right-8 -bottom-8 w-48 h-48 bg-white/5 rounded-full blur-2xl pointer-events-none" />
              
              {/* Content Section */}
              <div className="flex items-center gap-4 flex-1 min-w-0 mr-4 relative z-10">
                <div className={`p-3 bg-white/10 rounded-2xl backdrop-blur-md shrink-0 hidden sm:flex ${partner.iconColor}`}>
                  <Icon className="w-8 h-8 animate-pulse-gentle" />
                </div>
                
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-[10px] md:text-xs font-black uppercase tracking-wider bg-white/20 text-white px-2 py-0.5 rounded-full backdrop-blur-sm">
                      {partner.badge}
                    </span>
                  </div>
                  <h3 className="text-white font-black text-sm md:text-lg tracking-tight leading-tight truncate">
                    {partner.title}
                  </h3>
                  <p className="text-white/80 text-[11px] md:text-xs leading-tight mt-1 line-clamp-2 max-w-xl">
                    {partner.description}
                  </p>
                </div>
              </div>

              {/* Action Button & Large Icon for small screens */}
              <div className="flex flex-col items-end shrink-0 relative z-10">
                <button
                  type="button"
                  className="bg-white hover:bg-slate-50 active:scale-95 text-slate-900 font-black text-xs md:text-sm px-4 py-2.5 rounded-xl shadow-lg transition-all flex items-center gap-1.5 whitespace-nowrap"
                >
                  <span>{partner.ctaText}</span>
                  <ExternalLink className="w-3 h-3 text-slate-800" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Navigation Arrows - Only visible on hover */}
      <button
        onClick={prevSlide}
        className="carousel-control absolute left-3 top-1/2 -translate-y-1/2 p-1.5 rounded-xl bg-black/30 hover:bg-black/50 text-white/70 hover:text-white transition-all opacity-0 group-hover:opacity-100 z-20 focus:outline-none"
        title="Slide Anterior"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={nextSlide}
        className="carousel-control absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-xl bg-black/30 hover:bg-black/50 text-white/70 hover:text-white transition-all opacity-0 group-hover:opacity-100 z-20 focus:outline-none"
        title="Próximo Slide"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* Indicator Dots */}
      <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
        {carouselPartners.map((_, index) => (
          <button
            key={index}
            onClick={() => setActiveIndex(index)}
            className={`carousel-control h-1.5 rounded-full transition-all duration-300 ${
              index === activeIndex ? 'w-4 bg-white' : 'w-1.5 bg-white/40'
            }`}
            title={`Ir para o slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};
